/**
 * Vardiya Kalıbı servisi.
 * Şubeye özel kalıplar (örn. "Sabah Kasiyer", "Gece Rafçı").
 * Vardiya planlamasında tek tıkla kullanılır.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import type { FsTimestamp } from '@/types'

const KALIPLAR = 'vardiyaKaliplari'

export interface VardiyaKalibi {
  id: string
  subeId: string
  isim: string
  baslangic: string  // "HH:mm"
  bitis: string      // "HH:mm"
  pozisyonId?: string
  aktif: boolean
  olusturmaTarihi: FsTimestamp
  guncellenmeTarihi: FsTimestamp
}

export interface KalipOlusturmaData {
  subeId: string
  isim: string
  baslangic: string
  bitis: string
  pozisyonId?: string
}

function yeniId(): string {
  const db = getFirestoreDb()
  return doc(collection(db, KALIPLAR)).id
}

export async function tumKaliplariGetir(): Promise<VardiyaKalibi[]> {
  const db = getFirestoreDb()
  const snap = await getDocs(collection(db, KALIPLAR))
  const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as VardiyaKalibi)
  return liste.sort((a, b) => {
    if (a.subeId !== b.subeId) return a.subeId.localeCompare(b.subeId)
    return a.baslangic.localeCompare(b.baslangic)
  })
}

export async function subeKaliplariGetir(subeId: string): Promise<VardiyaKalibi[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, KALIPLAR), where('subeId', '==', subeId))
  const snap = await getDocs(q)
  const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as VardiyaKalibi)
  return liste.sort((a, b) => a.baslangic.localeCompare(b.baslangic))
}

export async function kalipGetir(id: string): Promise<VardiyaKalibi | null> {
  const db = getFirestoreDb()
  const docSnap = await getDoc(doc(db, KALIPLAR, id))
  if (!docSnap.exists()) return null
  return { id: docSnap.id, ...docSnap.data() } as VardiyaKalibi
}

export async function kalipOlustur(data: KalipOlusturmaData): Promise<string> {
  const db = getFirestoreDb()
  const id = yeniId()

  const docData: Record<string, unknown> = {
    subeId: data.subeId,
    isim: data.isim.trim(),
    baslangic: data.baslangic,
    bitis: data.bitis,
    aktif: true,
    olusturmaTarihi: nowServer(),
    guncellenmeTarihi: nowServer(),
  }
  if (data.pozisyonId) docData.pozisyonId = data.pozisyonId

  await setDoc(doc(db, KALIPLAR, id), docData)
  return id
}

export async function kalipGuncelle(
  id: string,
  data: Partial<KalipOlusturmaData>
): Promise<void> {
  const db = getFirestoreDb()
  const update: Record<string, unknown> = { guncellenmeTarihi: nowServer() }
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined) return
    update[key] = typeof value === 'string' ? value.trim() : value
  })
  await updateDoc(doc(db, KALIPLAR, id), update)
}

export async function kalipSil(id: string): Promise<void> {
  const db = getFirestoreDb()
  await updateDoc(doc(db, KALIPLAR, id), {
    aktif: false,
    guncellenmeTarihi: nowServer(),
  })
}

export async function kalipGeriYukle(id: string): Promise<void> {
  const db = getFirestoreDb()
  await updateDoc(doc(db, KALIPLAR, id), {
    aktif: true,
    guncellenmeTarihi: nowServer(),
  })
}

/**
 * Vardiya süresini saat olarak hesaplar.
 * Gece vardiyası (22:00 - 08:00) için ertesi günü dikkate alır.
 */
export function kalipSaati(kalip: { baslangic: string; bitis: string }): number {
  const [bSaat, bDk] = kalip.baslangic.split(':').map(Number)
  const [eSaat, eDk] = kalip.bitis.split(':').map(Number)

  let baslangicDk = bSaat * 60 + bDk
  let bitisDk = eSaat * 60 + eDk

  if (bitisDk <= baslangicDk) {
    bitisDk += 24 * 60 // gece vardiyası
  }

  return (bitisDk - baslangicDk) / 60
}
